import React, { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { telemetry } from '../../../../@vendor/Posthog';
import { useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { Button, Input, usePrompt } from '../../../../ui';
import { PopupTabs } from '../../../../ui/PopupTabs';
import { FlexScroller } from '../../../../ui/Scrollers';
import { Star } from '../../../../ui/Star';
import { useResolve } from '../../../Container';
import { useDashboardVersion } from '../../../account/AccountHooks';
import { AccountModel } from '../../../account/AccountModel';
import { Popup, PopupProps } from '../../../popups/Popup';
import { Copy, Flexer } from '../../elements/LayoutElements';
import { useTemplateData, useTemplateStarred } from '../TemplateHooks';
import { SearchEmpty } from '../gallery/SearchEmpty';
import { StarredEmpty } from '../gallery/StarredEmpty';
import { TeamEmpty } from '../gallery/TeamEmpty';
import { TemplateCard } from '../gallery/TemplateCard';
import { TemplateWithStarData } from '../gallery/TemplateListView';

const TemplateItem = React.memo<{ template: TemplateWithStarData, selected?: boolean }>(({ template, selected }) => {

    const [starred, toggleStarred] = useTemplateStarred(template);
    const version = useDashboardVersion();

    return <TemplateCard.Wrapper>
        <TemplateCard clickable selected={selected}>
            <TemplateCard.Image>
                <TemplateCard.Thumb id={template.id} thumbUrl={template.thumbUrl} />
            </TemplateCard.Image>
            <TemplateCard.Info>
                <TemplateCard.Header>
                    <TemplateCard.Label title={template.label}>
                        {template.label}
                    </TemplateCard.Label>
                </TemplateCard.Header>
                <TemplateCard.Body>
                    <Copy>{template.description}</Copy>
                </TemplateCard.Body>
                <TemplateCard.Footer>
                    <TemplateCard.TagList>
                        {template.tags.map(x => <TemplateCard.Tag key={x.id} color={x.color}>{x.label}</TemplateCard.Tag>)}
                    </TemplateCard.TagList>
                </TemplateCard.Footer>
            </TemplateCard.Info>
        </TemplateCard>
        {/* <TemplateCard.ActionMenu>
            <TemplateActionMenu {...{ template }} />
        </TemplateCard.ActionMenu> */}
        {version === 'next' &&
            <TemplateCard.StarButton active={starred}>
                <Star size="2" active={starred} onClick={toggleStarred} />
            </TemplateCard.StarButton>
        }
    </TemplateCard.Wrapper>;
});

export const editorTemplatePopup = (tag?: string) => ({ ui: EditorTemplatePopup, args: { tag } });

export const EditorTemplatePopup = ({ action, dismiss, args }: PopupProps) => {

    const account = useObserver(useResolve(AccountModel));
    const initialTag = args.tag as string;
    const version = useDashboardVersion();
    const { templates, tags } = useTemplateData(version);
    const [search, setSearch] = useState('');
    const [primary, setPrimary] = useState('all' as 'all' | 'team' | 'starred');
    const [secondary, setSecondary] = useState(initialTag || '');
    const setBoth = (p: 'all' | 'team' | 'starred', s: string) => {
        setPrimary(p);
        setSecondary(s);
    };

    useDebounce(() => search !== '' && telemetry.emit('template.search', { search }), 500);

    const selectedTag = useMemo(() => tags?.find(x => x.id === secondary || x.name === secondary), [tags, secondary]);
    const [selectedTemplate, setSelectedTemplate] = useState(null as TemplateWithStarData);

    const primaryFilteredTemplates = useMemo(
        () => templates?.filter(x => {
            if (search) return x.label.toLowerCase().includes(search.toLowerCase());
            if (primary === 'team') return !!x.team;
            if (primary === 'starred') return x.starred;
            return true;
        }),
        [templates, primary, search]);

    const secondaryFilteredTemplates = useMemo(
        () => primaryFilteredTemplates?.filter(x => !secondary || !!x.tags.includes(selectedTag)),
        [primaryFilteredTemplates, secondary]);

    const relevantTags = useMemo(
        () => tags?.filter(x => primaryFilteredTemplates?.some(t => t.tags.includes(x))),
        [tags, primaryFilteredTemplates]);

    const onSelect = () => {
        if (selectedTemplate) {
            action(selectedTemplate);
        }
    };

    const promptRef = usePrompt({
        onAction(confirm) {
            if (confirm) {
                onSelect();
            }
            else {
                dismiss();
            }
        }
    });

    return <Popup ref={promptRef} w={81} h={60}>
        <Flexer grow flex="column" gap="2">
            <Flexer flex="row" gap="1" align="center">
                <Flexer grow>
                    <Input placeholder="Search templates" value={search} onChange={setSearch} />
                </Flexer>
                <Popup.Close position="static" onAction={dismiss} />
            </Flexer>
            {!search &&
                <PopupTabs border="none" spacing="1.5" size="lg">
                    <PopupTabs.Item onClick={() => setBoth('all', '')} selected={primary === 'all'}>
                        All Templates
                    </PopupTabs.Item>
                    <PopupTabs.Item onClick={() => setBoth('team', '')} selected={primary === 'team'}>
                        {account.label} Templates
                    </PopupTabs.Item>
                    {version === 'next' &&
                        <PopupTabs.Item onClick={() => setBoth('starred', '')} selected={primary === 'starred'}>
                            Starred Templates
                        </PopupTabs.Item>
                    }
                </PopupTabs>
            }
            <Flexer flex="row" align="start" gap="2">
                <Flexer flex="row" gap=".5" wrap="wrap" grow>
                    {!!relevantTags?.length &&
                        <Button onAction={() => setSecondary('')} variant={!selectedTag ? 'primary' : 'common'}>
                            Everything
                        </Button>
                    }
                    {relevantTags?.map(x =>
                        <Button key={x.id} onAction={() => setSecondary(x.id)} variant={x.id === selectedTag?.id ? 'primary' : 'common'}>
                            {x.label}
                        </Button>
                    )}
                </Flexer>
                {/* <Flexer flex="row" center shrink>
                <Copy bold flush size=".875">Sort by:</Copy>
                <SortModeSelector options={sortOptions} value={sort} onChange={setSort} />
            </Flexer> */}
            </Flexer>
            <Flexer relative grow flex="column">
                {!!secondaryFilteredTemplates?.length &&
                    <Flexer absolute inset="-1" flex margin="0 0 1">
                        <FlexScroller autoHide={false}>
                            <Flexer padding="1 0 1 1">
                                {secondaryFilteredTemplates &&
                                    <Flexer flex="row" gap="2" wrap="wrap">
                                        {secondaryFilteredTemplates?.map(x =>
                                            <Flexer key={`${x.id}/${x.alias}`} onClick={() => setSelectedTemplate(x)} onDoubleClick={onSelect}>
                                                <TemplateItem template={x} selected={x === selectedTemplate} />
                                            </Flexer>
                                        )}
                                    </Flexer >
                                }
                            </Flexer>
                        </FlexScroller>
                    </Flexer>
                }
                {primary === 'team' && !primaryFilteredTemplates?.length && <TeamEmpty />}
                {primary === 'starred' && !primaryFilteredTemplates?.length && <StarredEmpty />}
                {!!search && !primaryFilteredTemplates?.length && <SearchEmpty />}
            </Flexer>
        </Flexer>
        <Popup.Actions>
            <Button
                disabled={!selectedTemplate}
                variant="primary"
                label="Insert Template"
                onAction={onSelect}
            />
        </Popup.Actions>
    </Popup>;
};
